import React, { useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import type { CustomElement } from './slate-types';
import { sanitizeRichEmailHtml } from '../../modules/emails/utils/sanitizeEmailBody';
import {
  isPlainPreTemplateHtml,
  preTemplateHtmlToPlainText,
  wrapPlainTextAsPreHtml,
} from '../../modules/emails/utils/unlockReplyForwardEditing';

type QuotedEl = CustomElement & { type: 'quoted-rich-html'; html: string };

interface QuotedRichHtmlElementProps {
  attributes: Record<string, unknown>;
  children: React.ReactNode;
  element: QuotedEl;
  editor: Parameters<typeof ReactEditor.findPath>[0];
}

const HIDDEN_VOID_CHILD: React.CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
};

const PLAIN_TEXT_AREA_STYLE: React.CSSProperties = {
  outline: 'none',
  margin: 0,
  padding: 0,
  color: 'inherit',
  display: 'block',
  width: '100%',
  minWidth: '100%',
  border: 'none',
  background: 'transparent',
  resize: 'vertical',
  overflowX: 'auto',
  overflowY: 'auto',
  whiteSpace: 'pre',
  wordBreak: 'normal',
  overflowWrap: 'normal',
  fontFamily: "ui-monospace, 'Courier New', Courier, monospace",
  fontSize: 13,
  lineHeight: 1.2,
  tabSize: 8,
  boxSizing: 'border-box',
};

/**
 * Unlocked reply/forward quoted body: keep original HTML + styles (like the iframe view),
 * with a contentEditable surface that syncs back into the Slate document.
 * Plain-text ops templates use a textarea (wrap=off) so lines are not soft-wrapped or split into divs.
 */
export function QuotedRichHtmlElement({ attributes, children, element, editor }: QuotedRichHtmlElementProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const html = element.html || '';
  const path = ReactEditor.findPath(editor, element);
  const preOnly = useMemo(() => isPlainPreTemplateHtml(html), [html]);
  const plainText = useMemo(() => preTemplateHtmlToPlainText(html), [html]);
  const lineCount = useMemo(() => plainText.split('\n').length, [plainText]);
  const textareaRows = Math.max(12, lineCount + 1);
  const textareaMinHeight = Math.max(200, lineCount * 16);

  useLayoutEffect(() => {
    if (preOnly) {
      const node = textareaRef.current;
      if (!node) return;
      if (document.activeElement === node) return;
      if (node.value !== plainText) {
        node.value = plainText;
      }
      return;
    }

    const node = innerRef.current;
    if (!node) return;
    if (document.activeElement === node) return;
    if (node.innerHTML !== html) {
      node.innerHTML = html;
    }
  }, [html, plainText, preOnly]);

  const syncPlainText = useCallback(
    (value: string) => {
      const next = wrapPlainTextAsPreHtml(value);
      if (next !== element.html) {
        Transforms.setNodes(editor, { html: next } as Partial<QuotedEl>, { at: path });
      }
    },
    [editor, path, element.html],
  );

  const syncFromDom = useCallback(() => {
    if (preOnly) {
      const node = textareaRef.current;
      if (!node) return;
      syncPlainText(node.value);
      return;
    }

    const node = innerRef.current;
    if (!node) return;
    const next = sanitizeRichEmailHtml(node.innerHTML);
    if (next !== element.html) {
      Transforms.setNodes(editor, { html: next } as Partial<QuotedEl>, { at: path });
    }
  }, [editor, path, element.html, preOnly, syncPlainText]);

  if (preOnly) {
    return (
      <div
        {...attributes}
        contentEditable={false}
        data-slate-quoted-rich-wrap
        data-plain-text-template
        style={{ display: 'block', width: '100%', maxWidth: '100%', margin: 0, padding: 0, overflowX: 'auto' }}
      >
        <textarea
          ref={textareaRef}
          wrap="off"
          rows={textareaRows}
          defaultValue={plainText}
          className="email-template-pre email-quoted-block-inner email-quoted-rich-editable email-template-textarea"
          style={{ ...PLAIN_TEXT_AREA_STYLE, minHeight: textareaMinHeight }}
          onBlur={syncFromDom}
        />
        <span style={HIDDEN_VOID_CHILD} aria-hidden>
          {children}
        </span>
      </div>
    );
  }

  return (
    <div
      {...attributes}
      contentEditable={false}
      data-slate-quoted-rich-wrap
      style={{ display: 'block', width: '100%', maxWidth: '100%', margin: 0, padding: 0 }}
    >
      <div
        ref={innerRef}
        contentEditable
        suppressContentEditableWarning
        className="email-quoted-block-inner email-quoted-rich-editable"
        style={{
          outline: 'none',
          margin: 0,
          padding: 0,
          color: 'inherit',
        }}
        onInput={syncFromDom}
        onBlur={syncFromDom}
      />
      <span style={HIDDEN_VOID_CHILD} aria-hidden>
        {children}
      </span>
    </div>
  );
}
